<template>
	<div :id="uid" class="copy-form-card">
		<div class="copy-form-card__status">
			<NotificationIcon
				:variant="notificationVariantIcon"
				ref="tooltipStatusRef"
				@mouseenter="tooltipStatusVisible = true"
				@focus="tooltipStatusVisible = true"
				@mouseleave="tooltipStatusVisible = false"
				@blur="tooltipStatusVisible = false"
			/>
		</div>

		<div class="copy-form-card__body">
			<div class="copy-form-card__main">
				<div class="copy-form-card__main-group">
					<span class="copy-form-card__name">
						{{ form.name }}
					</span>
					<span class="copy-form-card__revision">
						<Ellipsis :data="form.formRevision"></Ellipsis>
					</span>
					<span class="copy-form-card__type">
						{{ formPrintType(form.formPrintType) }}
					</span>
				</div>

				<div class="copy-form-card__description">
					<Ellipsis :data="form.description"></Ellipsis>
				</div>

				<div class="copy-form-card__customers">
					<span class="copy-form-card__customers-txt">
						{{ spanLabel }}
					</span>

					<div class="copy-form-card__customers-list">
						<Ellipsis :data="customerAllList"></Ellipsis>
					</div>
				</div>
			</div>

			<div class="copy-form-card__action">
				<Button
					className="btn--primary"
					:labelText="showLabelTextButton"
					:btnClick="
						() => {
							executeAction();
						}
					"
				/>
			</div>
		</div>

		<Tooltip
			:text="formStatus"
			:trackedRefName="'tooltipStatusRef'"
			:display="tooltipStatusVisible"
			:offset="[4, 4]"
			position="top-start"
		/>
	</div>
</template>
<script>
import Button from '@/components/forms/button/button';
import CustomerOptions from '@/mixins/customers/customer-options';
import NotificationIcon from '@/components/notifications/notification-icon/notification-icon';
import Tooltip from '@/components/shells/tooltip/tooltip';
import Ellipsis from '@/components/visual/ellipsis';
import { mapState, mapActions } from 'vuex';
import _ from 'lodash';
import { GET_CUSTOMER_OPTION_NAME } from '@/sections/options/options-mutation-type';

export default {
	name: 'CopyFormCard',
	mixins: [CustomerOptions],
	components: {
		Button,
		NotificationIcon,
		Tooltip,
		Ellipsis
	},
	props: {
		form: {
			type: Object,
			default: () => {}
		},
		actionType: {
			type: String,
			default: 'Copy'
		}
	},
	data() {
		return {
			tooltipStatusVisible: false,
			tooltipTitleVisible: false,
			tooltipCodeVisible: false
		};
	},

	computed: {
		...mapState('customerForms', ['customerId']),

		showLabelTextButton() {
			return this.actionType + ' Form';
		},
		spanLabel() {
			if (_.get(this, 'form.customers.length', 0) > 0) {
				let result = `Assigned to ${this.form.customers.length}`;
				if (this.form.customers.length > 1) {
					return `${result} Clients - `;
				} else {
					return `${result} Client - `;
				}
			} else {
				return 'No Clients assigned ';
			}
		},
		customerNames() {
			return this[GET_CUSTOMER_OPTION_NAME](
				this.form.customers.map(c => c.customerId)
			);
		},
		customerAllList() {
			if (this.customerNames) {
				return this.customerNames.join(', ');
			}
			return '';
		},
		customerPath() {
			if (_.startsWith(this.$route.path, '/clients/')) {
				return this.$route.path
					.split('/')
					.slice(0, 3)
					.join('/');
			}

			return '';
		},
		formStatus() {
			if (!this.form.active) {
				return 'Inactive';
			}
			return 'Active';
		},
		notificationVariantIcon() {
			if (!this.form.active) {
				return 'inactive';
			}
			return 'positive';
		}
	},
	methods: {
		...mapActions('form', ['assignCustomers']),

		executeAction() {
			if (this.actionType === 'Assign') {
				let customerAssingList = [
					{
						assigned: true,
						customerId:
							this.customerId || this.$route.params.customerId,
						enabled: true
					}
				];

				this.assignCustomerToForm(this.form.id, customerAssingList);
			} else {
				this.copyForm(this.form.formPrintType, this.form.id);
			}
		},
		async assignCustomerToForm(id, customers) {
			await this.assignCustomers({ id, customers }).then(isSuccessful => {
				isSuccessful && this.$store.dispatch('forms/reloadForms', true);
			});
		},
		copyForm(type, id) {
			this.$router.push({
				path: `${this.customerPath}/forms/copy/${id}/${
					type === 'I' ? 'impact' : 'laser'
				}`
			});
		},
		formPrintType(value) {
			if (value === 'I') {
				return 'Impact';
			}
			return 'Laser';
		}
	}
};
</script>
<style lang="scss" scoped>
@import '@/scss/5-objects/_cards.scss';

/**
    * Copy Forms Card
	* Can use SCSS variables = TRUE
 **/

.copy-form-card {
	@include card;
	display: grid;
	grid-template-columns: auto 1fr;
	column-gap: $gutter-md;

	&:not(:last-child) {
		margin-bottom: $gutter-lg;
	}

	&__body {
		position: relative;
		display: grid;
		grid-template-columns: 1fr auto;
		column-gap: $gutter-xl;
		@include body-text('regular');
	}

	&__main {
		display: flex;
		flex-direction: column;
	}

	&__main-group {
		display: grid;
		grid-template-columns: auto 1fr;
		column-gap: $gutter-md;
	}

	&__name {
		@include heading('md');
		line-height: 1.3;
	}

	&__revision {
		@include body-text('lg');
		line-height: 1.2;
		min-width: 0;
	}

	&__type {
		position: absolute;
		right: 0;
		@include body-text('regular-emphasis');
	}

	&__description {
		margin-top: $gutter-sm;
		max-width: 484px;
	}

	&__customers {
		display: grid;
		grid-template-columns: auto 1fr;
		column-gap: $gutter-sm;
		margin-top: $gutter-md;
	}

	&__customers-txt {
		color: $neutral-2;
	}

	&__customers-list {
		min-width: 0;
	}

	&__action {
		align-self: flex-end;
	}
}

::v-deep .copy-form-card__customers-list .tooltip {
	max-width: 310px;
}
</style>
